import {
  AppState, clearFormState,
  updateCurrentStateWithOTPInputFormProps,
  updateCurrentStateWithSendOTPFormProps, updateStandardFormErrorMessage
} from "../redux/AppStateSlice";
import { includes } from "lodash";
import { AppDispatch } from "../redux/store";
import { ClientContextObject } from "../redux";
import { getLogger } from "../logConfig";
import { local } from "../internationalization/Localization";

import { S } from "./sdk";

export enum OtpAuthenticationState {TargetSelection, CodeInvalidated, AuthenticatorInput}

const logger = getLogger("OtpAuthenticationSession");

export class OtpAuthenticationSession implements com.ts.mobile.sdk.UIAuthenticatorSessionOtp {
  title: string;
  username: string;
  possibleTargets: Array<com.ts.mobile.sdk.OtpTarget> | null;
  autoExecedTarget: com.ts.mobile.sdk.OtpTarget | null;
  errorMessage?: string;
  state: OtpAuthenticationState;
  allowUserToChangeTarget: boolean;
  mode: com.ts.mobile.sdk.AuthenticatorSessionMode | null;
  description: com.ts.mobile.sdk.AuthenticatorDescription | null;
  actionContext: com.ts.mobile.sdk.PolicyAction | null;
  uiContext: com.ts.mobile.sdk.UIContext | null;
  clientContext: ClientContextObject | null;
  dispatch: AppDispatch;

  constructor(title: string, username: string, possibleTargets: Array<com.ts.mobile.sdk.OtpTarget>, autoExecedTarget: com.ts.mobile.sdk.OtpTarget | null, dispatch: AppDispatch) {
    this.title = title;
    this.username = username;
    this.possibleTargets = possibleTargets;
    this.autoExecedTarget = autoExecedTarget;
    this.state = autoExecedTarget ? OtpAuthenticationState.AuthenticatorInput : OtpAuthenticationState.TargetSelection;
    this.dispatch = dispatch;
    this.errorMessage = undefined;
    this.allowUserToChangeTarget = false;
    this.mode = null;
    this.description = null;
    this.actionContext = null;
    this.uiContext = null;
    this.clientContext = null;
  }

  startSession(description: com.ts.mobile.sdk.AuthenticatorDescription, mode: com.ts.mobile.sdk.AuthenticatorSessionMode, actionContext: com.ts.mobile.sdk.PolicyAction | null, clientContext: object | null): void {
    logger.debug(`Started new ${S.AuthenticatorSessionMode[mode]} ${this.title} session`);
    this.description = description;
    this.mode = mode;
    this.actionContext = actionContext;
    this.uiContext = actionContext?.getUiContext() || null;
    this.clientContext = clientContext as any;
  }

  changeSessionModeToRegistrationAfterExpiration(): void {
    logger.debug("Changing session mode to registration after expiration");
    this.mode = S.AuthenticatorSessionMode.Registration;
  }

  endSession(): void {
    logger.debug(`Ended ${S.AuthenticatorSessionMode[this.mode as any]} ${this.title} session`);
    // const appState = store.getState().appState
    // const journey = appState.standardFormProps?.currentFormId
    // Retain userId for Reset Password Journey
    // if (journey === Journey.VALIDATE_CHALLENGE_QA){
    //   const userId = appState.redirectionUserId
    //   this.dispatch(clearFormState());
    //   this.dispatch( updateRedirectionUserId(userId));
    // } else  {
      this.dispatch(clearFormState());
   // }
  }

  promiseInput(): Promise<com.ts.mobile.sdk.InputOrControlResponse<com.ts.mobile.sdk.TargetBasedAuthenticatorInput<com.ts.mobile.sdk.OtpInput, com.ts.mobile.sdk.OtpTarget>>> {
    if (this.mode === S.AuthenticatorSessionMode.Authentication) {
      logger.debug(`Fetching ${this.title} authentication input`);
      if (this.state === OtpAuthenticationState.TargetSelection) {
        return this.promiseTargetSelectionInput();
      } else if (this.state === OtpAuthenticationState.CodeInvalidated) {
        return this.promiseTargetSelectionInput();
      } else if (this.state === OtpAuthenticationState.AuthenticatorInput) {
        return this.promiseOtpInput();
      } else {
        throw new Error("No OTP Authenticator Input implemented for Mode: " + this.mode + " and state: " + this.state);
      }
    } else if (this.mode === S.AuthenticatorSessionMode.Registration) {
      logger.debug(`Fetching ${this.title} registration input`);
      return Promise.reject("Otp registration unsupported");
    } else {
      throw new Error("No Password Authenticator Input implemented for Mode:" + this.mode);
    }
  }

  promiseRecoveryForError(error: com.ts.mobile.sdk.AuthenticationError, validRecoveries: Array<com.ts.mobile.sdk.AuthenticationErrorRecovery>, defaultRecovery: com.ts.mobile.sdk.AuthenticationErrorRecovery): Promise<com.ts.mobile.sdk.AuthenticationErrorRecovery> {
    logger.debug(`Starting error recovery with default recovery: ${defaultRecovery}; mode: [${S.AuthenticatorSessionMode[this.mode as any]}], error: [${error}]`);
    let errorData: any = error.getData();

    if (errorData?.additional_data?.locked) {
      logger.error("Authenticator locked", null);
    }

    //if submitted wrong otp
    if (includes(validRecoveries, S.AuthenticationErrorRecovery.RetryAuthenticator)) {
      this.errorMessage = local.get("otpInputForm.errors[0]");
      return Promise.resolve(S.AuthenticationErrorRecovery.RetryAuthenticator);
    } else {
      return Promise.resolve(defaultRecovery);
    }
  }

  setAvailableTargets(targets: Array<com.ts.mobile.sdk.OtpTarget> | null): void {
    this.possibleTargets = targets;
  }

  setGeneratedOtp(format: com.ts.mobile.sdk.OtpFormat | null, target: com.ts.mobile.sdk.OtpTarget | null): void {
    if (!target || this.allowUserToChangeTarget) {
      this.allowUserToChangeTarget = false;
      this.state = OtpAuthenticationState.TargetSelection;
    } else {
      this.state = OtpAuthenticationState.AuthenticatorInput;
    }
  }

  promiseTargetSelectionInput(): Promise<com.ts.mobile.sdk.InputOrControlResponse<com.ts.mobile.sdk.TargetBasedAuthenticatorInput<com.ts.mobile.sdk.OtpInput, com.ts.mobile.sdk.OtpTarget>>> {
    const menuItems = (this.possibleTargets || []).filter(target => target.getChannel() === 2).map(target => ({
      title: target.getDescription(),
      value: S.InputOrControlResponse.createInputResponse(S.TargetBasedAuthenticatorInput.createTargetSelectionRequest(target)),
      label: "Email:",
    }));
    const cancelRequest = S.InputOrControlResponse.createControlResponse(
      S.ControlRequest.create(S.ControlRequestType.CancelAuthenticator));
    const title = "One-Time Passcode";
    const instructions = "For your security, we need to verify your identity. We will send your One-Time Passcode (OTP) " +
      "to the email address you have listed with us, shown below.";
    return new Promise<com.ts.mobile.sdk.InputOrControlResponse<com.ts.mobile.sdk.TargetBasedAuthenticatorInput<com.ts.mobile.sdk.OtpInput, com.ts.mobile.sdk.OtpTarget>>>((resolve) => {
      this.dispatch(updateCurrentStateWithSendOTPFormProps({
        state: AppState.SEND_OTP,
        props: {targets: menuItems, instructions, cancelRequest, clientContext: this.clientContext as any, title, resolve},
      }))
    })
  }

  promiseOtpInput(): Promise<com.ts.mobile.sdk.InputOrControlResponse<com.ts.mobile.sdk.TargetBasedAuthenticatorInput<com.ts.mobile.sdk.OtpInput, com.ts.mobile.sdk.OtpTarget>>> {
    return new Promise<com.ts.mobile.sdk.InputOrControlResponse<com.ts.mobile.sdk.TargetBasedAuthenticatorInput<com.ts.mobile.sdk.OtpInput, com.ts.mobile.sdk.OtpTarget>>>((resolve) => {
      const onResendButtonClick = () => {
       // event.preventDefault();
        this.allowUserToChangeTarget = false;
        const targets = (this.possibleTargets || []).filter(target => target.getChannel() === 2);
        targets.length > 0 && resolve(S.InputOrControlResponse.createInputResponse(
          S.TargetBasedAuthenticatorInput.createTargetSelectionRequest(targets[0])))
      }
      this.dispatch(updateCurrentStateWithOTPInputFormProps({
        state: AppState.OTP_INPUT,
        props: {
          resolve,
          onResendButtonClick,
        }
      }))
      if(this.errorMessage) this.dispatch(updateStandardFormErrorMessage(this.errorMessage))
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  promiseCancelAction(options: any, clientContext: ClientContextObject | null) {
    logger.debug("About to abort journey after user clicked cancel");
    return new Promise((resolve => {
      resolve(S.ControlRequest.create(S.ControlRequestType.AbortAuthentication))
    }));
  }
}
